// Account-scoped backup + durable outbox. Failed saves stay queued across reloads;
// a successful GET cannot overwrite an edit or resurrect a pending deletion.
import type { Dashboard } from '../types'
import { apiFetch } from './http'
import { flagUnauthorized } from './auth'
import { TENANT } from '../tenant'

const API_BASE = import.meta.env.VITE_API_URL ?? ''
const PREFIX = `${TENANT.id}_dashboards_v2:`
interface Pending { dashboard: Dashboard | null; revision: string }
interface Backup { dashboards: Dashboard[]; pending: Record<string, Pending> }
const memory = new Map<string, Backup>()
const syncing = new Map<string, { promise: Promise<void>; signal?: AbortSignal }>()
const key = (owner: string) => `${PREFIX}${encodeURIComponent(owner)}`

function read(owner: string): Backup {
  try {
    const raw = localStorage.getItem(key(owner))
    if (raw) {
      const data = JSON.parse(raw) as Backup
      if (Array.isArray(data.dashboards) && data.pending && typeof data.pending === 'object') return data
    }
  } catch { /* use this tab's backup when browser storage is unavailable */ }
  return memory.get(owner) ?? { dashboards: [], pending: {} }
}

function write(owner: string, backup: Backup) {
  memory.set(owner, backup)
  try { localStorage.setItem(key(owner), JSON.stringify(backup)) } catch { /* server save still works */ }
}

export function dashboardBackup(owner: string): Dashboard[] { return read(owner).dashboards }
export function hasPendingDashboards(owner: string): boolean { return Object.keys(read(owner).pending).length > 0 }

export function stageDashboard(dash: Dashboard, owner: string): void {
  const backup = read(owner)
  const exists = backup.dashboards.some((d) => d.id === dash.id)
  write(owner, {
    dashboards: exists ? backup.dashboards.map((d) => d.id === dash.id ? dash : d) : [...backup.dashboards, dash],
    pending: { ...backup.pending, [dash.id]: { dashboard: dash, revision: crypto.randomUUID() } },
  })
}

export function stageDashboardDeletion(id: string, owner: string): void {
  const backup = read(owner)
  write(owner, { dashboards: backup.dashboards.filter((d) => d.id !== id),
    pending: { ...backup.pending, [id]: { dashboard: null, revision: crypto.randomUUID() } } })
}

class DashboardRequestError extends Error {
  constructor(readonly status: number) { super(`Dashboard request failed (HTTP ${status}).`) }
}

async function checked(response: Response): Promise<void> {
  if (response.status === 401) flagUnauthorized()
  if (!response.ok) throw new DashboardRequestError(response.status)
}

// One writer per account, including deletes. A newer local revision cannot be
// cleared by an older response or arrive at the server before that response.
export function syncDashboards(owner: string, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) return Promise.reject(new DOMException('Aborted', 'AbortError'))
  const active = syncing.get(owner)
  if (active) {
    // React may replace a load effect while its old writer is still aborting.
    // Wait for that writer to retire, then start one under the NEW lifetime.
    // Never resurrect a cancelled caller, or retry a real 401/403 here.
    if (active.signal?.aborted) {
      return active.promise.catch((error: unknown) => {
        if (!(error && typeof error === 'object' && 'name' in error && error.name === 'AbortError')) throw error
      }).then(() => syncDashboards(owner, signal))
    }
    return active.promise
  }
  const job = (async () => {
    // MPD-94: one blocked op must not stall the others. Each board has one pending entry (a
    // later save or delete replaces it, so a save after a delete of the same board cannot be
    // overtaken by it). Send every entry; a failed one stays pending for the next sync and the
    // first failure is reported once all were tried. An expired session (401) stops at once.
    const failed = new Map<string, string>()
    let firstError: unknown = null
    for (;;) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
      const entry = Object.entries(read(owner).pending).find(([id, op]) => failed.get(id) !== op.revision)
      if (!entry) break
      const [id, op] = entry
      try {
        // Production's edge policy refuses DELETE/PUT/PATCH, so a deletion is a POST too.
        const response = await apiFetch(`${API_BASE}/api/dashboards${op.dashboard ? '' : `/${encodeURIComponent(id)}/delete`}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: op.dashboard ? JSON.stringify(op.dashboard) : undefined,
          signal,
        })
        // Deletion is idempotent: a previous response may have been lost.
        if (op.dashboard || response.status !== 404) await checked(response)
      } catch (error) {
        if (signal?.aborted || (error instanceof Error && error.name === 'AbortError')
          || (error instanceof DashboardRequestError && error.status === 401)) throw error
        failed.set(id, op.revision)
        firstError ??= error
        continue
      }
      const latest = read(owner)
      if (latest.pending[id]?.revision === op.revision) {
        const pending = { ...latest.pending }
        delete pending[id]
        write(owner, { ...latest, pending })
      }
    }
    if (firstError !== null) throw firstError
  })().finally(() => { syncing.delete(owner) })
  syncing.set(owner, { promise: job, signal })
  return job
}

export interface DashboardLoad {
  dashboards: Dashboard[]
  syncError: string | null
}

export async function fetchDashboards(owner: string, signal?: AbortSignal): Promise<DashboardLoad> {
  let syncError: string | null = null
  try {
    await syncDashboards(owner, signal)
  } catch (error) {
    // A blocked/offline write must not hide existing server boards. The outbox
    // stays authoritative for edits/deletes; the caller still displays failure.
    // An expired/aborted account must never start another read.
    if (signal?.aborted || (error instanceof DashboardRequestError && error.status === 401)
      || (error instanceof Error && error.name === 'AbortError')) throw error
    syncError = error instanceof Error ? error.message : 'Could not sync dashboard changes.'
  }
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
  const response = await apiFetch(`${API_BASE}/api/dashboards`, { signal })
  await checked(response)
  const server: unknown = await response.json()
  if (!Array.isArray(server) || !server.every((d) => d && typeof d.id === 'string' && Array.isArray(d.cards))) {
    throw new Error('The saved dashboards could not be read.')
  }
  const backup = read(owner)
  const merged = new Map<string, Dashboard>((server as Dashboard[]).map((d) => [d.id, d]))
  for (const [id, op] of Object.entries(backup.pending)) {
    if (op.dashboard) merged.set(id, op.dashboard)
    else merged.delete(id)
  }
  const dashboards = [...merged.values()]
  write(owner, { ...backup, dashboards })
  return { dashboards, syncError }
}

// Older releases used one anonymous browser mirror. Offer explicit recovery so
// rollout of durable storage does not silently erase existing hand-built boards.
export function legacyDashboardBackup(): Dashboard[] {
  try {
    const data: unknown = JSON.parse(localStorage.getItem(`${TENANT.id}_dashboards`) ?? '[]')
    return Array.isArray(data) ? data.filter((d) => d && typeof d.id === 'string' && Array.isArray(d.cards)) : []
  } catch { return [] }
}

export function clearLegacyDashboardBackup(): void { localStorage.removeItem(`${TENANT.id}_dashboards`) }

export function clearDashboardMemory(): void { memory.clear() }
